import {
  DEFAULT_ENGINE_TYPE_BY_KEY,
  ENGINE_TYPE_TEMPLATES,
  resolveInterestEngine,
  type EngineChurnOverride,
  type EngineCycleOverride,
  type EngineDecayOverride,
  type EngineHotOverride,
  type EnginePromotionOverride,
  type InterestEngineOverride,
  type InterestEngineType,
  type InterestTagConfig,
  type PromoInterestKey,
  type ResolvedInterestEngine,
} from '@kawai/shared';
import { notificationRecipientsPortalUrl } from '../lib/hotRecipients.js';
import { ENGINE_TYPE_LABELS } from '../lib/segment.js';

/**
 * M-74 (promo): 商材 1 キー分の engine 設定フォーム。
 *
 * 差分保存が原則（M-81 の eventLevels 全量焼き込みの轍を踏まない）:
 * - グループ（promotion / decay / cycle / hot）単位の「既定に追従 / 上書き」二値で管理し、
 *   既定に追従のグループは保存 payload に**含めない** → 既定テンプレートの将来変更が
 *   未上書きテナントへ自動追従する
 * - タイプ変更はテンプレート値を各グループへ「上書き」として展開する（明示値化）。
 *   実行時解決は常に「明示上書き → 商材別既定」の 2 層（resolveInterestEngine）
 */

export type GroupMode = 'default' | 'custom';

export interface EngineForm {
  /** 表示中のタイプ（未上書きは商材既定）。既定と異なる場合のみ保存する。 */
  type: InterestEngineType;
  promotion: {
    mode: GroupMode;
    /** 「累積昇格しない」= cumulativeDisabled: true で保存（既定ありの商材を対象外へ落とす明示表現）。 */
    cumulativeDisabled: boolean;
    windowDays: string;
    touchCount: string;
    couponClaimToDesire: boolean;
  };
  decay: {
    mode: GroupMode;
    /** 「減衰せず追跡」（タイプ②）= desireDays を明示 null で保存する。 */
    desireTrack: boolean;
    desireDays: string;
    interestTrack: boolean;
    interestDays: string;
  };
  cycle: {
    mode: GroupMode;
    kind: 'expiry' | 'interval';
    months: string;
    expirySourced: boolean;
    leadMonths: string;
  };
  hot: {
    mode: GroupMode;
    enabled: boolean;
    /** M-76: HOT 判定・自動フォローの閾値（数値の文字列入力）。 */
    surgeWindowDays: string;
    surgeEventCount: string;
    followUpAfterDays: string;
    reopenCooldownDays: string;
  };
  /** M-75: タイプ③の離反検知（呼び戻し）。 */
  churn: {
    mode: GroupMode;
    enabled: boolean;
    /** 通常間隔の倍率（小数可）。 */
    multiplier: string;
    minIntervals: string;
    maxIntervals: string;
  };
}

const numStr = (v: number | null | undefined): string =>
  typeof v === 'number' ? String(v) : '';

/** 解決済み engine 値からフォームの入力値部分を作る（mode は呼び出し側で決める）。 */
function valuesFromResolved(resolved: ResolvedInterestEngine) {
  return {
    promotion: {
      cumulativeDisabled: resolved.cumulative === null,
      windowDays: numStr(resolved.cumulative?.windowDays),
      touchCount: numStr(resolved.cumulative?.touchCount),
      couponClaimToDesire: resolved.couponClaimToDesire,
    },
    decay: {
      desireTrack: resolved.decay.desireDays === null,
      desireDays: numStr(resolved.decay.desireDays),
      interestTrack: resolved.decay.interestDays === null,
      interestDays: numStr(resolved.decay.interestDays),
    },
    cycle: {
      kind: resolved.cycle?.kind ?? ('interval' as const),
      months: numStr(resolved.cycle?.months),
      expirySourced: resolved.cycle?.expirySourced ?? false,
      leadMonths: numStr(resolved.cycle?.leadMonths ?? 0),
    },
    hot: {
      enabled: resolved.hot.enabled,
      surgeWindowDays: numStr(resolved.hot.surgeWindowDays),
      surgeEventCount: numStr(resolved.hot.surgeEventCount),
      followUpAfterDays: numStr(resolved.hot.followUpAfterDays),
      reopenCooldownDays: numStr(resolved.hot.reopenCooldownDays),
    },
    churn: {
      enabled: resolved.churn?.enabled ?? false,
      multiplier: numStr(resolved.churn?.multiplier),
      minIntervals: numStr(resolved.churn?.minIntervals),
      maxIntervals: numStr(resolved.churn?.maxIntervals),
    },
  };
}

/** 保存済み config からフォーム初期値を作る（override があるグループのみ custom）。 */
export function buildEngineForm(
  key: PromoInterestKey,
  config: InterestTagConfig | null,
): EngineForm {
  const ov = config?.engines?.[key];
  const resolved = resolveInterestEngine(key, config ?? undefined);
  const v = valuesFromResolved(resolved);
  return {
    type: ov?.type ?? DEFAULT_ENGINE_TYPE_BY_KEY[key],
    promotion: { mode: ov?.promotion ? 'custom' : 'default', ...v.promotion },
    decay: { mode: ov?.decay ? 'custom' : 'default', ...v.decay },
    cycle: { mode: ov?.cycle ? 'custom' : 'default', ...v.cycle },
    hot: { mode: ov?.hot ? 'custom' : 'default', ...v.hot },
    churn: { mode: ov?.churn ? 'custom' : 'default', ...v.churn },
  };
}

/**
 * タイプ変更時のテンプレート適用: テンプレ値を各グループの「上書き」としてフォームへ
 * 展開する。そのタイプで表示されないグループ（cycle はタイプ①のみ / hot はタイプ②のみ）
 * は「既定に追従」のままにする — 非表示のまま明示上書きが焼き込まれると UI から解除
 * できなくなるため。テンプレの cumulative: null は「累積昇格しない」チェックとして展開
 * され、保存時に `cumulativeDisabled: true` の明示値になる（表示と実効値の一致）。
 */
export function applyEngineTypeTemplate(type: InterestEngineType): EngineForm {
  const tpl = ENGINE_TYPE_TEMPLATES[type];
  const v = valuesFromResolved({ type, ...tpl });
  return {
    type,
    promotion: { mode: 'custom', ...v.promotion },
    decay: { mode: 'custom', ...v.decay },
    cycle: { mode: type === 'periodic' && tpl.cycle ? 'custom' : 'default', ...v.cycle },
    hot: { mode: type === 'considered' ? 'custom' : 'default', ...v.hot },
    churn: { mode: type === 'frequent' && tpl.churn ? 'custom' : 'default', ...v.churn },
  };
}

/**
 * 既定タイプへ戻すときのリセット: 全グループを「既定に追従」へ戻す（テンプレの代表値を
 * 明示上書きとして焼き込まない — 代表値は商材別既定と一致するとは限らないため）。
 */
export function resetEngineForm(key: PromoInterestKey): EngineForm {
  return buildEngineForm(key, null);
}

const parseIntField = (s: string, label: string, min: number): number | string => {
  const n = Number(s.trim());
  if (!Number.isInteger(n) || n < min)
    return `${label}は ${min} 以上の整数で入力してください`;
  return n;
};

/** M-75: churn.multiplier 用（小数可 / min 以上）。 */
const parseNumField = (s: string, label: string, min: number): number | string => {
  const n = Number(s.trim());
  if (!Number.isFinite(n) || s.trim() === '' || n < min)
    return `${label}は ${min} 以上の数値で入力してください`;
  return n;
};

/**
 * フォームから保存用の差分 override を組み立てる。
 * custom グループのみ明示値を条件付き代入で構築し（undefined 代入は絶対にしない —
 * callable の undefined→null 変換で decay の null 意味論が誤爆するのを防ぐ）、
 * 上書きが 1 つも無ければ undefined（payload から商材キーごと落とす）。
 */
export function engineOverrideFromForm(
  key: PromoInterestKey,
  f: EngineForm,
): { override?: InterestEngineOverride; error?: string } {
  const ov: InterestEngineOverride = {};
  if (f.type !== DEFAULT_ENGINE_TYPE_BY_KEY[key]) ov.type = f.type;

  if (f.promotion.mode === 'custom') {
    const p: EnginePromotionOverride = {};
    if (f.promotion.cumulativeDisabled) {
      // 「累積昇格しない」= 明示無効化（既定ありの商材でも cumulative を null に落とす）。
      p.cumulativeDisabled = true;
    } else {
      // 累積規則は windowDays × touchCount のペアでのみ意味を持つ。空欄ペアを黙って
      // 「既定に追従」にすると表示と保存値が食い違うため、明示入力か無効化を要求する。
      const w = parseIntField(f.promotion.windowDays, '鮮度窓（日）', 1);
      const c = parseIntField(f.promotion.touchCount, '接触回数', 1);
      if (typeof w === 'string' || typeof c === 'string') {
        return {
          error:
            '鮮度窓と接触回数を両方入力してください（累積昇格を止める場合は「累積昇格しない」をチェック）',
        };
      }
      p.windowDays = w;
      p.touchCount = c;
    }
    p.couponClaimToDesire = f.promotion.couponClaimToDesire;
    ov.promotion = p;
  }

  if (f.decay.mode === 'custom') {
    const d: EngineDecayOverride = {};
    if (f.decay.desireTrack) d.desireDays = null;
    else {
      const n = parseIntField(f.decay.desireDays, 'D 鮮度（日）', 1);
      if (typeof n === 'string') return { error: n };
      d.desireDays = n;
    }
    if (f.decay.interestTrack) d.interestDays = null;
    else {
      const n = parseIntField(f.decay.interestDays, 'I 鮮度（日）', 1);
      if (typeof n === 'string') return { error: n };
      d.interestDays = n;
    }
    ov.decay = d;
  }

  // cycle / hot はそのタイプで表示されるときのみ保存する（非表示グループの上書きが
  // 残置されると UI から解除できないため。タイプ変更時の古い上書きも保存時に掃除される）。
  if (f.type === 'periodic' && f.cycle.mode === 'custom') {
    const months = parseIntField(f.cycle.months, '周期（か月）', 1);
    if (typeof months === 'string') return { error: months };
    const lead = parseIntField(f.cycle.leadMonths || '0', '先行（か月）', 0);
    if (typeof lead === 'string') return { error: lead };
    const c: EngineCycleOverride = {
      kind: f.cycle.kind,
      months,
      expirySourced: f.cycle.expirySourced,
      leadMonths: lead,
    };
    ov.cycle = c;
  }

  if (f.type === 'considered' && f.hot.mode === 'custom') {
    const h: EngineHotOverride = { enabled: f.hot.enabled };
    // M-76: 閾値は有効時のみ保存する（無効なら判定も走らないため差分を増やさない）。
    if (f.hot.enabled) {
      const win = parseIntField(f.hot.surgeWindowDays, '急増の観測窓（日）', 1);
      if (typeof win === 'string') return { error: win };
      const cnt = parseIntField(f.hot.surgeEventCount, '急増の接触日数', 1);
      if (typeof cnt === 'string') return { error: cnt };
      const follow = parseIntField(f.hot.followUpAfterDays, '自動フォロー（日）', 1);
      if (typeof follow === 'string') return { error: follow };
      const cooldown = parseIntField(
        f.hot.reopenCooldownDays || '0',
        '再燃の冷却（日）',
        0,
      );
      if (typeof cooldown === 'string') return { error: cooldown };
      if (cnt > win + 1) {
        return { error: '急増の接触日数は観測窓の日数を超えないようにしてください' };
      }
      h.surgeWindowDays = win;
      h.surgeEventCount = cnt;
      h.followUpAfterDays = follow;
      h.reopenCooldownDays = cooldown;
    }
    ov.hot = h;
  }

  // churn はタイプ③のみ表示・保存（cycle / hot と同じ非表示グループの掃除規約）。
  if (f.type === 'frequent' && f.churn.mode === 'custom') {
    const c: EngineChurnOverride = { enabled: f.churn.enabled };
    if (f.churn.enabled) {
      const mul = parseNumField(f.churn.multiplier, '逸脱倍率', 1);
      if (typeof mul === 'string') return { error: mul };
      const minI = parseIntField(f.churn.minIntervals, '最少間隔数', 1);
      if (typeof minI === 'string') return { error: minI };
      const maxI = parseIntField(f.churn.maxIntervals, '対象間隔数', 1);
      if (typeof maxI === 'string') return { error: maxI };
      if (maxI < minI) {
        return { error: '対象間隔数は最少間隔数以上にしてください' };
      }
      c.multiplier = mul;
      c.minIntervals = minI;
      c.maxIntervals = maxI;
    }
    ov.churn = c;
  }

  return Object.keys(ov).length > 0 ? { override: ov } : {};
}

// ---------- 表示ヘルパ ----------

function promotionSummary(r: ResolvedInterestEngine): string {
  const cumulative = r.cumulative
    ? `${r.cumulative.windowDays}日以内に${r.cumulative.touchCount}回接触`
    : '累積昇格なし';
  return `${cumulative} / クーポンD直行 ${r.couponClaimToDesire ? '○' : '×'}`;
}

function decaySummary(r: ResolvedInterestEngine): string {
  const d =
    r.decay.desireDays === null ? 'D 追跡（減衰なし）' : `D ${r.decay.desireDays}日`;
  const i =
    r.decay.interestDays === null ? 'I 追跡（減衰なし）' : `I ${r.decay.interestDays}日`;
  return `${d} / ${i}`;
}

function cycleSummary(r: ResolvedInterestEngine): string {
  if (!r.cycle) return '周期なし';
  const kind = r.cycle.kind === 'expiry' ? '期限起点' : '前回実施起点';
  const expiry = r.cycle.expirySourced ? '・期限実データ優先' : '';
  return `${kind} ${r.cycle.months}か月・${r.cycle.leadMonths}か月前に再セット${expiry}`;
}

function hotSummary(r: ResolvedInterestEngine): string {
  if (!r.hot.enabled) return '通知しない';
  return `通知する（宛先はポータルの通知先登録 / 急増 = ${r.hot.surgeWindowDays}日で${r.hot.surgeEventCount}日接触・${r.hot.followUpAfterDays}日無対応で自動フォロー・再燃冷却${r.hot.reopenCooldownDays}日）`;
}

function churnSummary(r: ResolvedInterestEngine): string {
  if (!r.churn) return '検知しない';
  return `通常間隔の${r.churn.multiplier}倍で呼び戻し対象（直近${r.churn.maxIntervals}間隔の中央値・最少${r.churn.minIntervals}間隔）`;
}

const smallInputClass =
  'w-full rounded-lg bg-white px-3.5 py-2 text-body text-ink focus:outline-hidden focus:ring-2 focus:ring-brand disabled:text-ink-3 disabled:opacity-60';

interface GroupHeaderProps {
  title: string;
  mode: GroupMode;
  defaultSummary: string;
  onToggle: (custom: boolean) => void;
}

/** グループ見出し + 「既定に追従 / 上書き」トグル + 既定値の要約表示。 */
export function GroupHeader({ title, mode, defaultSummary, onToggle }: GroupHeaderProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <span className="text-label font-medium text-ink-2">{title}</span>
        {mode === 'default' && (
          <span className="ml-2 text-label text-ink-3">既定: {defaultSummary}</span>
        )}
      </div>
      <label className="flex shrink-0 items-center gap-1 text-label text-ink-2">
        <input
          type="checkbox"
          className="text-brand focus:ring-brand"
          checked={mode === 'custom'}
          onChange={(e) => onToggle(e.target.checked)}
        />
        上書き
      </label>
    </div>
  );
}

export interface InterestEngineSectionProps {
  interestKey: PromoInterestKey;
  form: EngineForm;
  onChange: (next: EngineForm) => void;
}

export function InterestEngineSection({
  interestKey,
  form,
  onChange,
}: InterestEngineSectionProps) {
  // 既定値の表示は「保存済み上書き込みの解決値」ではなく商材既定（config なし解決）を出す
  // — 「既定に追従」に戻した時に何が効くかをそのまま見せるため。
  const productDefault = resolveInterestEngine(interestKey);
  const overriddenGroups = [
    form.promotion,
    form.decay,
    form.cycle,
    form.hot,
    form.churn,
  ].filter((g) => g.mode === 'custom').length;

  const changeType = (type: InterestEngineType): void => {
    if (type === form.type) return;
    if (type === DEFAULT_ENGINE_TYPE_BY_KEY[interestKey]) {
      // 既定タイプへ戻す = 代表テンプレの焼き込みではなく「既定に追従」へのリセット
      //（テンプレ代表値は商材別既定と一致するとは限らない）。
      const ok = window.confirm(
        `タイプを既定（${ENGINE_TYPE_LABELS[type]}）に戻し、全グループを「既定に追従」へリセットしますか？\n（保存するまで確定しません）`,
      );
      if (ok) onChange(resetEngineForm(interestKey));
      return;
    }
    const ok = window.confirm(
      `タイプを「${ENGINE_TYPE_LABELS[type]}」に変更し、テンプレート値を適用しますか？\n（各グループが上書きになります。保存するまで確定しません）`,
    );
    if (ok) onChange(applyEngineTypeTemplate(type));
  };

  return (
    <details className="rounded-lg bg-white">
      <summary className="cursor-pointer px-3 py-1.5 text-label text-ink-2">
        昇格エンジン設定（{ENGINE_TYPE_LABELS[form.type]}
        {overriddenGroups > 0 ? ` / 上書き ${overriddenGroups} 件` : ''}）
      </summary>
      <div className="space-y-3 px-3 py-2">
        {/* タイプ */}
        <label className="flex items-center justify-between gap-2">
          <span className="text-label font-medium text-ink-2">タイプ</span>
          <select
            className="rounded-lg bg-surface-2 px-3.5 py-2 text-body text-ink focus:outline-hidden focus:ring-2 focus:ring-brand"
            value={form.type}
            onChange={(e) => changeType(e.target.value as InterestEngineType)}
          >
            {(Object.keys(ENGINE_TYPE_LABELS) as InterestEngineType[]).map((t) => (
              <option key={t} value={t}>
                {ENGINE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>

        {/* promotion: I→D 昇格 */}
        <div className="space-y-2 rounded-lg bg-surface-2 px-4 py-3">
          <GroupHeader
            title="I→D 昇格"
            mode={form.promotion.mode}
            defaultSummary={promotionSummary(productDefault)}
            onToggle={(custom) =>
              onChange({
                ...form,
                promotion: { ...form.promotion, mode: custom ? 'custom' : 'default' },
              })
            }
          />
          {form.promotion.mode === 'custom' && (
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-label text-ink-2">
                <input
                  type="checkbox"
                  className="text-brand focus:ring-brand"
                  checked={form.promotion.cumulativeDisabled}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      promotion: {
                        ...form.promotion,
                        cumulativeDisabled: e.target.checked,
                      },
                    })
                  }
                />
                累積昇格しない（鮮度窓×接触回数の I→D 判定を行わない）
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-label text-ink-3">
                  鮮度窓（日）
                  <input
                    type="number"
                    min={1}
                    max={365}
                    disabled={form.promotion.cumulativeDisabled}
                    className={smallInputClass}
                    value={form.promotion.windowDays}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        promotion: { ...form.promotion, windowDays: e.target.value },
                      })
                    }
                  />
                </label>
                <label className="block text-label text-ink-3">
                  接触回数
                  <input
                    type="number"
                    min={1}
                    max={20}
                    disabled={form.promotion.cumulativeDisabled}
                    className={smallInputClass}
                    value={form.promotion.touchCount}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        promotion: { ...form.promotion, touchCount: e.target.value },
                      })
                    }
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-label text-ink-2">
                <input
                  type="checkbox"
                  className="text-brand focus:ring-brand"
                  checked={form.promotion.couponClaimToDesire}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      promotion: {
                        ...form.promotion,
                        couponClaimToDesire: e.target.checked,
                      },
                    })
                  }
                />
                クーポン取得で D 直行
              </label>
            </div>
          )}
        </div>

        {/* decay: 評価時減衰 */}
        <div className="space-y-2 rounded-lg bg-surface-2 px-4 py-3">
          <GroupHeader
            title="減衰"
            mode={form.decay.mode}
            defaultSummary={decaySummary(productDefault)}
            onToggle={(custom) =>
              onChange({
                ...form,
                decay: { ...form.decay, mode: custom ? 'custom' : 'default' },
              })
            }
          />
          {form.decay.mode === 'custom' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-label text-ink-2">
                  <input
                    type="checkbox"
                    className="text-brand focus:ring-brand"
                    checked={form.decay.desireTrack}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        decay: { ...form.decay, desireTrack: e.target.checked },
                      })
                    }
                  />
                  D は追跡（減衰しない）
                </label>
                <label className="block text-label text-ink-3">
                  D 鮮度（日）
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    disabled={form.decay.desireTrack}
                    className={smallInputClass}
                    value={form.decay.desireDays}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        decay: { ...form.decay, desireDays: e.target.value },
                      })
                    }
                  />
                </label>
              </div>
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-label text-ink-2">
                  <input
                    type="checkbox"
                    className="text-brand focus:ring-brand"
                    checked={form.decay.interestTrack}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        decay: { ...form.decay, interestTrack: e.target.checked },
                      })
                    }
                  />
                  I は追跡（減衰しない）
                </label>
                <label className="block text-label text-ink-3">
                  I 鮮度（日）
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    disabled={form.decay.interestTrack}
                    className={smallInputClass}
                    value={form.decay.interestDays}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        decay: { ...form.decay, interestDays: e.target.value },
                      })
                    }
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* cycle: タイプ①のみ */}
        {form.type === 'periodic' && (
          <div className="space-y-2 rounded-lg bg-surface-2 px-4 py-3">
            <GroupHeader
              title="周期再セット（日次バッチが自動発火 / M-75）"
              mode={form.cycle.mode}
              defaultSummary={cycleSummary(productDefault)}
              onToggle={(custom) =>
                onChange({
                  ...form,
                  cycle: { ...form.cycle, mode: custom ? 'custom' : 'default' },
                })
              }
            />
            {form.cycle.mode === 'custom' && (
              <div className="space-y-1">
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-label text-ink-3">
                    起点
                    <select
                      className={smallInputClass}
                      value={form.cycle.kind}
                      onChange={(e) =>
                        onChange({
                          ...form,
                          cycle: {
                            ...form.cycle,
                            kind: e.target.value as 'expiry' | 'interval',
                          },
                        })
                      }
                    >
                      <option value="expiry">期限起点（車検期限など）</option>
                      <option value="interval">前回実施起点</option>
                    </select>
                  </label>
                  <label className="block text-label text-ink-3">
                    周期（か月）
                    <input
                      type="number"
                      min={1}
                      max={120}
                      className={smallInputClass}
                      value={form.cycle.months}
                      onChange={(e) =>
                        onChange({
                          ...form,
                          cycle: { ...form.cycle, months: e.target.value },
                        })
                      }
                    />
                  </label>
                  <label className="block text-label text-ink-3">
                    再セット先行（か月前）
                    <input
                      type="number"
                      min={0}
                      max={24}
                      className={smallInputClass}
                      value={form.cycle.leadMonths}
                      onChange={(e) =>
                        onChange({
                          ...form,
                          cycle: { ...form.cycle, leadMonths: e.target.value },
                        })
                      }
                    />
                  </label>
                  <label className="mt-4 flex items-center gap-2 text-label text-ink-2">
                    <input
                      type="checkbox"
                      className="text-brand focus:ring-brand"
                      checked={form.cycle.expirySourced}
                      onChange={(e) =>
                        onChange({
                          ...form,
                          cycle: { ...form.cycle, expirySourced: e.target.checked },
                        })
                      }
                    />
                    期限実データを優先
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* hot: タイプ②のみ */}
        {form.type === 'considered' && (
          <div className="space-y-2 rounded-lg bg-surface-2 px-4 py-3">
            <GroupHeader
              title="HOT案件（担当者通知 + 自動配信の排他制御）"
              mode={form.hot.mode}
              defaultSummary={hotSummary(productDefault)}
              onToggle={(custom) =>
                onChange({
                  ...form,
                  hot: { ...form.hot, mode: custom ? 'custom' : 'default' },
                })
              }
            />
            {form.hot.mode === 'custom' && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-label text-ink-2">
                  <input
                    type="checkbox"
                    className="text-brand focus:ring-brand"
                    checked={form.hot.enabled}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        hot: { ...form.hot, enabled: e.target.checked },
                      })
                    }
                  />
                  HOT 案件を担当者に通知する
                </label>
                <p className="text-label text-ink-3">
                  通知先（メール / LINE）は
                  <a
                    className="text-brand underline"
                    href={notificationRecipientsPortalUrl()}
                  >
                    テナントポータルの「通知先登録」
                  </a>
                  で「HOT 案件」にチェックを付けた人です（N-15c / ここでは設定しません）。
                </p>
                {/*
                  M-76: 判定・運用の閾値。有効時のみ入力させる（無効なら判定が走らない）。
                */}
                {form.hot.enabled && (
                  <>
                    <p className="text-label text-ink-3">
                      LINE
                      通知は、上のメールと同じアドレスが登録された管理者アカウントへ送られます。
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-label text-ink-3">
                        急増の観測窓（日）
                        <input
                          type="number"
                          min={1}
                          className={smallInputClass}
                          value={form.hot.surgeWindowDays}
                          onChange={(e) =>
                            onChange({
                              ...form,
                              hot: { ...form.hot, surgeWindowDays: e.target.value },
                            })
                          }
                        />
                      </label>
                      <label className="block text-label text-ink-3">
                        急増とみなす接触日数
                        <input
                          type="number"
                          min={1}
                          className={smallInputClass}
                          value={form.hot.surgeEventCount}
                          onChange={(e) =>
                            onChange({
                              ...form,
                              hot: { ...form.hot, surgeEventCount: e.target.value },
                            })
                          }
                        />
                      </label>
                      <label className="block text-label text-ink-3">
                        自動フォロー（無対応 日数）
                        <input
                          type="number"
                          min={1}
                          className={smallInputClass}
                          value={form.hot.followUpAfterDays}
                          onChange={(e) =>
                            onChange({
                              ...form,
                              hot: { ...form.hot, followUpAfterDays: e.target.value },
                            })
                          }
                        />
                      </label>
                      <label className="block text-label text-ink-3">
                        再燃の冷却（日 / 0 で即時）
                        <input
                          type="number"
                          min={0}
                          className={smallInputClass}
                          value={form.hot.reopenCooldownDays}
                          onChange={(e) =>
                            onChange({
                              ...form,
                              hot: { ...form.hot, reopenCooldownDays: e.target.value },
                            })
                          }
                        />
                      </label>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* churn: タイプ③のみ（M-75 離反検知 / 判定は評価時 = セグメント「呼び戻し対象」） */}
        {form.type === 'frequent' && (
          <div className="space-y-2 rounded-lg bg-surface-2 px-4 py-3">
            <GroupHeader
              title="離反検知（呼び戻し / M-75）"
              mode={form.churn.mode}
              defaultSummary={churnSummary(productDefault)}
              onToggle={(custom) =>
                onChange({
                  ...form,
                  churn: { ...form.churn, mode: custom ? 'custom' : 'default' },
                })
              }
            />
            {form.churn.mode === 'custom' && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-label text-ink-2">
                  <input
                    type="checkbox"
                    className="text-brand focus:ring-brand"
                    checked={form.churn.enabled}
                    onChange={(e) =>
                      onChange({
                        ...form,
                        churn: { ...form.churn, enabled: e.target.checked },
                      })
                    }
                  />
                  来店間隔の逸脱を検知する（顧客ごとの実績分布から判定）
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block text-label text-ink-3">
                    逸脱倍率
                    <input
                      type="number"
                      min={1}
                      max={10}
                      step={0.1}
                      disabled={!form.churn.enabled}
                      className={smallInputClass}
                      value={form.churn.multiplier}
                      onChange={(e) =>
                        onChange({
                          ...form,
                          churn: { ...form.churn, multiplier: e.target.value },
                        })
                      }
                    />
                  </label>
                  <label className="block text-label text-ink-3">
                    最少間隔数
                    <input
                      type="number"
                      min={1}
                      max={20}
                      disabled={!form.churn.enabled}
                      className={smallInputClass}
                      value={form.churn.minIntervals}
                      onChange={(e) =>
                        onChange({
                          ...form,
                          churn: { ...form.churn, minIntervals: e.target.value },
                        })
                      }
                    />
                  </label>
                  <label className="block text-label text-ink-3">
                    対象間隔数
                    <input
                      type="number"
                      min={1}
                      max={24}
                      disabled={!form.churn.enabled}
                      className={smallInputClass}
                      value={form.churn.maxIntervals}
                      onChange={(e) =>
                        onChange({
                          ...form,
                          churn: { ...form.churn, maxIntervals: e.target.value },
                        })
                      }
                    />
                  </label>
                </div>
                <p className="text-label text-ink-3">
                  最終来店から「直近の来店間隔の中央値 × 逸脱倍率」日を超えると
                  セグメント条件「呼び戻し対象」に一致します（間隔が最少間隔数に満たない
                  顧客は対象外）。
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
